import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useStandaloneReports } from '../../hooks/useStandaloneReports';
import { computeReport } from '../../util/questionnaire-computations';
import type { RootState } from '../../types/graph';
import { Switch } from '@/components/ui/switch';

export const Report = () => {
  const { userResponses, kb, graph, consultation } = useSelector(
    (state: RootState) => state,
  );
  const standaloneReports = useStandaloneReports();
  const report = computeReport({
    userResponses,
    kbResponses: kb.responses,
    graph,
    formulas: kb.formulas,
    standaloneReports,
    consultation,
  });
  const [parsedTreeMode, setParsedTreeMode] = useState(false);

  return (
    <div className="bg-[var(--dbg-bg)] flex flex-col p-4 text-[var(--dbg-text)]">
      <div className="mb-4 flex items-center gap-2">
        <Switch
          checked={parsedTreeMode}
          onCheckedChange={(checked) => setParsedTreeMode(checked)}
          className="bg-[var(--dbg-border)] data-[checked]:bg-[var(--dbg-switch)]"
        />
        <label
          className="text-[var(--dbg-text-muted)] text-sm cursor-pointer"
          onClick={() => setParsedTreeMode(!parsedTreeMode)}
        >
          Parsed Tree Mode
        </label>
      </div>
      {parsedTreeMode ? (
        <div className="font-mono text-sm text-[var(--dbg-text)]">
          {(() => {
            // First pass: process concatenations
            const processedReport = [];
            for (let i = 0; i < report.length; i++) {
              if (report[i].concatenate) {
                continue;
              }

              let concatenatedValue = report[i].value;
              let concatenatedClassification = report[i].classification;

              let j = i + 1;
              while (j < report.length && report[j].concatenate) {
                concatenatedValue += report[j].value;
                concatenatedClassification += ' | ' + report[j].classification;
                j++;
              }

              processedReport.push({
                ...report[i],
                value: concatenatedValue,
                classification: concatenatedClassification,
                originalIndex: i,
              });
            }

            // Second pass: normalize indents first
            let lastKnownIndent = 0;
            const normalizedReport = processedReport.map((r) => {
              const currentIndent = r.indent ?? lastKnownIndent;
              if (r.indent !== undefined) {
                lastKnownIndent = r.indent;
              }
              return { ...r, normalizedIndent: currentIndent };
            });

            // Third pass: build ancestor chains and check if each is last child
            const ancestorChains = normalizedReport.map((r, i) => {
              const chain = [];
              const currentIndent = r.normalizedIndent;

              for (let level = 0; level < currentIndent; level++) {
                let ancestorIdx = -1;
                for (let k = i - 1; k >= 0; k--) {
                  if (normalizedReport[k].normalizedIndent === level) {
                    ancestorIdx = k;
                    break;
                  }
                }
                chain.push(ancestorIdx);
              }
              return chain;
            });

            // Fourth pass: check which items are last children
            const isLastChildMap = normalizedReport.map((r, i) => {
              const currentIndent = r.normalizedIndent;
              for (let j = i + 1; j < normalizedReport.length; j++) {
                if (normalizedReport[j].normalizedIndent === currentIndent) {
                  return false;
                }
                if (normalizedReport[j].normalizedIndent < currentIndent) {
                  break;
                }
              }
              return true;
            });

            // Fifth pass: render with tree structure
            return normalizedReport.map((r, i) => {
              const currentIndent = r.normalizedIndent;
              const isLastChild = isLastChildMap[i];
              const ancestors = ancestorChains[i];

              let treePrefix = '';
              for (let level = 0; level < currentIndent; level++) {
                if (level === currentIndent - 1) {
                  treePrefix += isLastChild ? '└─ ' : '├─ ';
                } else {
                  const nextLevelAncestorIdx = ancestors[level + 1];
                  const ancestorIsLastChild =
                    nextLevelAncestorIdx >= 0
                      ? isLastChildMap[nextLevelAncestorIdx]
                      : true;
                  treePrefix += ancestorIsLastChild ? '   ' : '│  ';
                }
              }

              const classifications = r.classification.split(' | ');
              const hasMultipleClassifications = classifications.length > 1;
              const displayClassification = hasMultipleClassifications
                ? `${classifications[0]} | …`
                : r.classification;

              const displayValue = r.value.replace(/^[\s+]+/, '');

              return (
                <div
                  key={r.originalIndex}
                  className="flex justify-between"
                  style={{
                    paddingTop: r.indent === 0 ? '20px' : '0',
                  }}
                >
                  <span className="whitespace-pre text-[var(--dbg-text)]">
                    <span className="text-[var(--dbg-text-dim)]">
                      {treePrefix}
                    </span>
                    {displayValue}
                  </span>
                  <span
                    title={
                      hasMultipleClassifications ? r.classification : undefined
                    }
                    className={`text-[var(--dbg-text-secondary)] ${
                      hasMultipleClassifications ? 'cursor-help' : ''
                    }`}
                  >
                    {displayClassification}
                  </span>
                </div>
              );
            });
          })()}
        </div>
      ) : (
        <div className="flex flex-col gap-0.5">
          {report
            .sort((a, b) => a.classification.localeCompare(b.classification))
            .map((r, i) => (
              <code
                key={i}
                className="bg-[var(--dbg-bg-secondary)] text-[var(--dbg-text)] font-mono text-xs px-1 rounded"
              >
                {r.indent}/ {r.classification}: {r.value}
              </code>
            ))}
        </div>
      )}
    </div>
  );
};
